import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import {
  SNOOP_SEARCH_LIMIT,
  SNOOP_SEARCH_MIN_CHARS,
  fetchSnoopCoreFollowers,
  fetchSnoopSummary,
  updateArtistMappingStatus,
  updateSnoopMappingStatus,
} from '../api/snoopClient'
import {
  SNOOP_UNIFIED_ALL_OPTIONS,
  parseSnoopUnifiedStatus,
  shouldRemoveSnoopUnifiedRow,
  snoopUnifiedOptimisticPatch,
  snoopUnifiedValue,
} from '../utils/snoop/statusOptions'

function SnoopCoreFollowersPanel({ payload }) {
  const followers = Array.isArray(payload?.followers) ? payload.followers : []
  if (!followers.length) {
    return (
      <p className="text-sm text-gray-500">
        No CORE sources have followed this user in snoop history.
      </p>
    )
  }
  return (
    <div className="max-w-3xl">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        CORE followers ({followers.length})
      </p>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">CORE username</th>
              <th className="px-3 py-2 font-medium">Full name</th>
              <th className="px-3 py-2 font-medium">IG</th>
            </tr>
          </thead>
          <tbody>
            {followers.map((f) => {
              const uname = String(f?.source_username || '').replace(/^@/, '')
              const uid = String(f?.source_user_id || '').replace(/\.0$/, '')
              const key = `${uid}|${f?.followed_date || ''}|${uname}`
              return (
                <tr key={key} className="border-t border-gray-100">
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-gray-700">
                    {f?.followed_date || '—'}
                  </td>
                  <td className="px-3 py-1.5 font-medium text-gray-900">
                    {uname ? `@${uname}` : uid || '—'}
                  </td>
                  <td className="max-w-[14rem] truncate px-3 py-1.5 text-gray-600" title={f?.source_full_name || ''}>
                    {f?.source_full_name || '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    {uname ? (
                      <a
                        href={`https://www.instagram.com/${encodeURIComponent(uname)}/`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--encore-blue)] hover:underline"
                      >
                        open
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function SnoopSummaryPage() {
  const [snoopSourceFilter, setSnoopSourceFilter] = useState({
    active: false,
    selected: [],
    applyServerAnd: false,
    matchMode: 'or',
    followScope: 'all',
  })
  /**
   * Search runs server-side: the default browse only loads New Follows ≥ 1, and the
   * unfloored snapshot (~260k rows) is far too large to ship and filter in the browser,
   * so quiet-week accounts are unreachable by a client-side match. Below the minimum
   * length the box falls back to filtering the loaded page.
   */
  const [snoopQuery, setSnoopQuery] = useState('')
  const searchActive = snoopQuery.length >= SNOOP_SEARCH_MIN_CHARS

  // All-follows scope (or search) needs quiet-week rows; New-follows scope does not.
  const includeZeroNewFollows =
    searchActive ||
    (snoopSourceFilter.active && snoopSourceFilter.followScope !== 'new')

  const fetchData = useCallback(
    () =>
      fetchSnoopSummary({
        minNewFollows: includeZeroNewFollows ? 0 : 1,
        allFollowsAnd: snoopSourceFilter.applyServerAnd
          ? snoopSourceFilter.selected
          : undefined,
        q: searchActive ? snoopQuery : undefined,
      }),
    [includeZeroNewFollows, snoopSourceFilter, searchActive, snoopQuery],
  )

  const onSnoopSourcesAndMultiChange = useCallback((next) => {
    const active = Boolean(next?.active)
    const selected = Array.isArray(next?.selected) ? next.selected : []
    const applyServerAnd = Boolean(next?.applyServerAnd)
    const matchMode = next?.matchMode === 'and' ? 'and' : 'or'
    const followScope = next?.followScope === 'new' ? 'new' : 'all'
    setSnoopSourceFilter((prev) => {
      const sameActive = prev.active === active
      const sameAnd = prev.applyServerAnd === applyServerAnd
      const sameMode = prev.matchMode === matchMode
      const sameScope = prev.followScope === followScope
      const sameSelected =
        prev.selected.length === selected.length &&
        prev.selected.every((name, i) => name === selected[i])
      if (sameActive && sameAnd && sameMode && sameScope && sameSelected) return prev
      return { active, selected, applyServerAnd, matchMode, followScope }
    })
  }, [])

  const onSnoopQueryChange = useCallback((next) => {
    setSnoopQuery(String(next || '').trim())
  }, [])

  const historyFetcher = useCallback(async (row) => {
    const igUserId = row?.['IG User ID']
    if (igUserId == null || String(igUserId).trim() === '') {
      return { followers: [] }
    }
    return fetchSnoopCoreFollowers(igUserId)
  }, [])

  const renderExpandedHistory = useCallback(
    (payload) => <SnoopCoreFollowersPanel payload={payload} />,
    [],
  )

  /**
   * One control for both statuses. Snoop Status and Artist Status are mutually
   * exclusive by policy — a snoop status means "not an artist" — so options are
   * namespaced `snoop:` / `artist:` and routed to the matching table on save.
   */
  const editableStatus = useMemo(
    () => ({
      Status: {
        target: 'snoop_mapping',
        uidColumn: 'IG User ID',
        usernameColumn: 'IG Username',
        options: SNOOP_UNIFIED_ALL_OPTIONS,
        allowBlank: false,
        removeAfterSave: true,
        getValue: snoopUnifiedValue,
        optimisticPatch: snoopUnifiedOptimisticPatch,
        shouldRemoveRow: shouldRemoveSnoopUnifiedRow,
        onSave: async ({ status, uid, row, snapshotDate }) => {
          const { target, value } = parseSnoopUnifiedStatus(status)
          if (target === 'snoop') {
            await updateSnoopMappingStatus({
              igUserId: uid,
              status: value,
              igUsername: row?.['IG Username'],
              igFullName: row?.['IG Full Name'],
              snapshotDate,
            })
            return
          }
          if (target === 'artist') {
            await updateArtistMappingStatus({
              uid,
              status: value,
              platform: 'ig',
              snapshotDate,
              mirrorSnoopArtistStatus: true,
              artist: row?.['IG Full Name'] || row?.['IG Username'],
              instagram: row?.['IG Username'],
            })
            return
          }
          throw new Error(`Unrecognized status option: ${status}`)
        },
      },
    }),
    [],
  )

  const subtitle = useMemo(() => {
    if (searchActive) {
      return `Searching the full snapshot · all New Follows counts · top ${SNOOP_SEARCH_LIMIT} by relevance`
    }
    if (snoopSourceFilter.active) {
      const n = snoopSourceFilter.selected.length
      const scope =
        snoopSourceFilter.followScope === 'new' ? 'new follows' : 'all follows'
      const match = snoopSourceFilter.applyServerAnd
        ? 'AND'
        : snoopSourceFilter.matchMode.toUpperCase()
      return `${n} source${n === 1 ? '' : 's'} · ${scope} · ${match}`
    }
    return 'New follows from snoop sources (unclassified only) · New Follows ≥ 1 · expand a row for CORE follower history'
  }, [snoopSourceFilter, searchActive])

  return (
    <SummaryDashboard
      title="Snoop"
      subtitle={subtitle}
      fetchData={fetchData}
      defaultSort={{ column: 'New Follows', direction: 'desc' }}
      searchPlaceholder="Search username, name, UID, status…"
      editableStatus={editableStatus}
      hiddenColumns={['Artist Status']}
      layout="snoop"
      linkLabel="IG"
      onSnoopSourcesAndMultiChange={onSnoopSourcesAndMultiChange}
      serverSearch={searchActive}
      onServerQueryChange={onSnoopQueryChange}
      expandableHistory
      historyFetcher={historyFetcher}
      renderExpandedHistory={renderExpandedHistory}
      historyExpandTitle="Show CORE followers"
      historyLoadingLabel="Loading CORE followers…"
    />
  )
}
